import React, { useMemo, useCallback, useState } from 'react';
import { TransactionCategory, StoredPaymentMethodSelect } from '@shipstation/financial-services-ui-components';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { updatePaymentMethod } from '../../../../../financial-services-ui/api';
import { FinancialServicesUIProviderWrapper } from '../../../../../financial-services-ui/financial-services-ui-provider-wrapper';
import { FsuiContextProvider } from '../../../../../financial-services-ui/fsui-context-provider';
import fsuiStyles from '../../../../../financial-services-ui/fsui-styles.module.less';
import { getPaymentMethodsFromArray } from '../../../../../financial-services-ui/mapping';
import { useWallet } from '../../../../../wallet/hooks/use-wallet';
import { usePaymentAndSubscriptionMegaContext } from '../../context';
import { paymentTableMessages } from '../../messages';

/** @typedef {import('@shipstation/financial-services-ui-components').StoredPaymentMethod} StoredPaymentMethod */
/** @typedef {import('../../../../../financial-services-ui/api').PaymentMethodDetails} PaymentMethodDetails */

/**
 * Shows a success notification when payment method is updated successfully
 * @returns {void}
 */
const notifySuccess = () => {
  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_SUCCESS,
    title: <FormattedMessage {...paymentTableMessages.paymentMethodUpdateSuccessTitle} />,
    content: <FormattedMessage {...paymentTableMessages.paymentMethodUpdateSuccessMessage} />,
  });
};

/**
 * Shows an error notification when payment method update fails
 * @returns {void}
 */
const notifyError = () => {
  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_ERROR,
    title: <FormattedMessage {...paymentTableMessages.paymentMethodUpdateErrorTitle} />,
    content: <FormattedMessage {...paymentTableMessages.paymentMethodUpdateErrorMessage} />,
  });
};

/**
 * A payment method picker component that allows users to select and update payment methods
 * for different transaction categories (subscription, wallet funding, etc.). Handles the
 * payment method update process including loading states, error handling, and notifications.
 * @param {object} props - Component props
 * @param {TransactionCategory} props.transactionCategory - The transaction category this picker is for
 * @param {(() => void)} [props.onChange] - Optional callback function called after successful payment method update
 * @param {boolean} [props.disabled] - Whether the picker should be disabled
 * @returns {React.ReactElement} The payment method picker component
 */
export const PaymentMethodPicker = ({ transactionCategory, onChange, disabled }) => {
  const { walletId } = useWallet();
  const { reloadData, paymentMethodDetails } = usePaymentAndSubscriptionMegaContext();
  // wallet depends on homeCountry, seller depends on subscriptionCurrency
  const { defaultCurrency, subscriptionCurrency } = useSelector((state) => state.seller);
  const currencyCode = transactionCategory === TransactionCategory.Subscription ? subscriptionCurrency : defaultCurrency;

  /** @type {StoredPaymentMethod} */
  const initialValue = useMemo(() => {
    return paymentMethodDetails.find((method) => method.assignedPreferences?.includes(transactionCategory));
  }, [transactionCategory, paymentMethodDetails]);

  const [onChangeLoading, setOnChangeLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  /** @type {(paymentMethod: StoredPaymentMethod) => void} */
  const onChangePicker = useCallback(
    (paymentMethod) => {
      if (!paymentMethod) {
        return;
      }
      setOnChangeLoading(true);
      setHasError(false);

      const payload = {
        paymentMethodId: paymentMethod.id,
        assignedPreferences: [transactionCategory],
        currencyCode,
        walletUpdateRequest:
          transactionCategory === TransactionCategory.AddFunds
            ? {
                walletId,
              }
            : undefined,
      };

      updatePaymentMethod(payload)
        .then((result) => {
          onChange?.();

          if (!result.success) {
            throw new Error(result.errorMessage);
          }

          notifySuccess();
          setHasError(false);
          reloadData();
        })
        .catch(() => {
          setHasError(true);
          notifyError();
        })
        .finally(() => {
          setOnChangeLoading(false);
        });
    },
    [transactionCategory, currencyCode, walletId, onChange, reloadData],
  );

  const isDisabled = onChangeLoading || paymentMethodDetails.length === 1 || disabled;
  const hasContent = paymentMethodDetails.length > 0;
  const reconstructedPaymentMethods = useMemo(() => getPaymentMethodsFromArray(paymentMethodDetails), [paymentMethodDetails]);

  return (
    <FsuiContextProvider currencyCode={currencyCode} shouldSkipSessionCreation>
      <FinancialServicesUIProviderWrapper>
        <StoredPaymentMethodSelect
          className={classnames(hasContent ? fsuiStyles.picker : fsuiStyles['picker-no-content'], {
            [fsuiStyles['picker-error']]: hasError,
          })}
          disabled={isDisabled}
          hasError={hasError}
          initialValue={initialValue}
          isFullWidth={false}
          isLoading={onChangeLoading}
          name={`payment-method-picker-${transactionCategory}`}
          onChange={onChangePicker}
          paymentMethods={reconstructedPaymentMethods}
          transactionCategory={transactionCategory}
        />
      </FinancialServicesUIProviderWrapper>
    </FsuiContextProvider>
  );
};
